import { useState } from 'react';
import { useOffline } from '../contexts/OfflineContext';
import { usePresentAppError } from './usePresentAppError';
import { addAdhocRouteStop } from '../services/firebase/FunctionsService';
import type { IDataAdapter } from '../adapters/data/IDataAdapter';
import type { AppNotification } from '../core/models/index';

type T = Record<string, string>;
type Push = (fields: Partial<AppNotification>, source?: string) => AppNotification | null;

type Opts = {
  t: T;
  push: Push;
  adapter: IDataAdapter;
  userId: string;
  houseId: string;
  routeId: string;
  selfCollect: boolean;
  canAuthorizeNow: boolean;
  canCompleteNow: boolean;
};

/** Authorize departure, ad-hoc stops, and manual collection completion. */
export function useBankerRouteAuthorize({
  t,
  push,
  adapter,
  userId,
  houseId,
  routeId,
  selfCollect,
  canAuthorizeNow,
  canCompleteNow,
}: Opts) {
  const { online } = useOffline();
  const presentAppError = usePresentAppError();
  const [authBusy, setAuthBusy] = useState(false);
  const [completeBusy, setCompleteBusy] = useState(false);
  const [completeConfirmOpen, setCompleteConfirmOpen] = useState(false);
  const [completeReason, setCompleteReason] = useState('');
  const [adhocBusy, setAdhocBusy] = useState(false);
  const [adhocName, setAdhocName] = useState('');
  const [adhocAddr, setAdhocAddr] = useState('');

  const authorizeDeparture = async () => {
    if (!adapter.authorizeRouteDeparture || authBusy || !canAuthorizeNow) return;
    setAuthBusy(true);
    try {
      await adapter.authorizeRouteDeparture(userId, {
        selfCollect: Boolean(selfCollect),
      });
      push({
        cat: 'routes',
        sev: 'success',
        title: selfCollect
          ? (t.routeSelfCollectStarted ?? t.routeDepartAuthorized)
          : t.routeDepartAuthorized,
        body: selfCollect
          ? (t.routeDepartAuthorizedSelfCollectBody ?? t.routeDepartAuthorizedBody ?? '')
          : (t.routeDepartAuthorizedBody ?? t.routeDepartReleaseHint ?? ''),
      });
    } catch (err) {
      const key = err && typeof err === 'object' && 'userMessageKey' in err
        ? String((err as { userMessageKey?: string }).userMessageKey ?? '')
        : '';
      const msg = String((err as { message?: string })?.message ?? '');
      const blocked = key === 'routeDepartBlockedInProgress'
        || msg.includes('in progress')
        || msg.includes('route_departure_in_progress');
      push({
        cat: 'routes',
        sev: 'danger',
        title: t.err_generic,
        body: blocked
          ? (t.routeDepartBlockedInProgress ?? t.routeDepartAuthorizeFailed ?? '')
          : (t.routeDepartAuthorizeFailed ?? ''),
      });
    } finally {
      setAuthBusy(false);
    }
  };

  const openCompleteConfirm = () => {
    if (!adapter.completeRouteCollection || completeBusy || !canCompleteNow) return;
    setCompleteReason('');
    setCompleteConfirmOpen(true);
  };

  const closeCompleteConfirm = () => {
    if (completeBusy) return;
    setCompleteConfirmOpen(false);
    setCompleteReason('');
  };

  const completeCollection = async () => {
    if (!adapter.completeRouteCollection || completeBusy || !canCompleteNow) return;
    const reason = completeReason.trim();
    if (reason.length < 3) {
      push({
        cat: 'routes',
        sev: 'warn',
        title: t.routeCompleteReasonRequired ?? t.err_generic,
        body: t.routeCompleteConfirmHint ?? '',
      });
      return;
    }
    setCompleteBusy(true);
    try {
      await adapter.completeRouteCollection({
        actorId: userId,
        reason,
        confirmed: true,
      });
      setCompleteConfirmOpen(false);
      setCompleteReason('');
      push({
        cat: 'routes',
        sev: 'success',
        title: t.routeCollectionCompleted ?? 'Collection completed',
        body: t.routeCompleteManualBody
          ?? 'Route marked completed. You can authorize a new departure when ready.',
      });
    } catch {
      push({
        cat: 'routes',
        sev: 'danger',
        title: t.err_generic,
        body: t.routeCompleteFailed ?? 'Could not mark the route as completed.',
      });
    } finally {
      setCompleteBusy(false);
    }
  };

  const addAdhoc = async () => {
    if (adhocBusy || !online || !routeId || !adhocAddr.trim()) return;
    setAdhocBusy(true);
    try {
      await addAdhocRouteStop({
        houseId,
        routeId,
        name: adhocName.trim() || undefined,
        addr: adhocAddr.trim(),
        geocode: true,
      });
      setAdhocName('');
      setAdhocAddr('');
      push({
        cat: 'routes',
        sev: 'success',
        title: t.adhocStopAdded ?? 'Stop added',
        body: adhocAddr.trim(),
      });
    } catch (err) {
      const { copy } = presentAppError(err, { t });
      push({
        cat: 'routes',
        sev: 'danger',
        title: copy?.title || t.adhocStopFailed || t.err_generic,
        body: copy?.hint || '',
      });
    } finally {
      setAdhocBusy(false);
    }
  };

  return {
    authBusy,
    completeBusy,
    completeConfirmOpen,
    completeReason,
    setCompleteReason,
    adhocBusy,
    adhocName,
    setAdhocName,
    adhocAddr,
    setAdhocAddr,
    authorizeDeparture,
    openCompleteConfirm,
    closeCompleteConfirm,
    completeCollection,
    addAdhoc,
  };
}
